// ErrorBoundary.js - Catches errors in lazy-loaded components and provides recovery options
import React, { Component } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { recordError } from '../../utils/rum';

/**
 * Error Boundary component for catching JavaScript errors in child components
 * Particularly useful for wrapping lazy-loaded components that may fail to load
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Send error to AWS CloudWatch RUM
    recordError(error);

    // Log error for debugging
    console.error('ErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo?.componentStack);

    // Call optional onError callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return typeof this.props.fallback === 'function'
          ? this.props.fallback({
              error: this.state.error,
              retry: this.handleRetry
            })
          : this.props.fallback;
      }

      // Default error UI
      return (
        <ErrorFallback
          error={this.state.error}
          onRetry={this.handleRetry}
          componentName={this.props.componentName}
          compact={this.props.compact}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Default error fallback UI component
 */
const ErrorFallback = ({ error, onRetry, componentName, compact = false }) => {
  const isChunkError = error?.message?.includes('Loading chunk') ||
                       error?.message?.includes('Failed to fetch') ||
                       error?.name === 'ChunkLoadError';

  if (compact) {
    return (
      <div
        style={{
          padding: '12px 16px',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}
      >
        <AlertTriangle size={18} color="#dc2626" />
        <span style={{ fontSize: '13px', color: '#991b1b', flex: 1 }}>
          {isChunkError ? 'Failed to load component' : 'Something went wrong'}
        </span>
        <button
          onClick={onRetry}
          style={{
            padding: '4px 12px',
            fontSize: '12px',
            background: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <RefreshCw size={12} />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '40px 20px',
        background: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '8px',
        textAlign: 'center'
      }}
    >
      <AlertTriangle size={40} color="#dc2626" style={{ marginBottom: '16px' }} />

      <h3 style={{
        margin: '0 0 8px 0',
        fontSize: '16px',
        fontWeight: 600,
        color: '#991b1b'
      }}>
        {isChunkError
          ? `Failed to load ${componentName || 'component'}`
          : 'Something went wrong'
        }
      </h3>

      <p style={{
        margin: '0 0 20px 0',
        fontSize: '14px',
        color: '#b91c1c'
      }}>
        {isChunkError
          ? 'This might be due to a network issue. Please check your connection and try again.'
          : error?.message || 'An unexpected error occurred.'
        }
      </p>

      <button
        onClick={onRetry}
        style={{
          padding: '8px 20px',
          fontSize: '14px',
          fontWeight: 500,
          background: '#dc2626',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          transition: 'background 0.2s'
        }}
        onMouseOver={(e) => e.target.style.background = '#b91c1c'}
        onMouseOut={(e) => e.target.style.background = '#dc2626'}
      >
        <RefreshCw size={16} />
        Try Again
      </button>
    </div>
  );
};

/**
 * Higher-order component to wrap any component with an error boundary
 */
export const withErrorBoundary = (WrappedComponent, options = {}) => {
  const WithErrorBoundary = (props) => (
    <ErrorBoundary {...options}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  WithErrorBoundary.displayName = `WithErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return WithErrorBoundary;
};

export { ErrorFallback };
export default ErrorBoundary;
